import { analytics } from '../../utils/analytics.js';

function byId<T extends HTMLElement>(id: string, type: new () => T): T {
  const element = document.getElementById(id);
  if (!(element instanceof type)) throw new Error(`Endless Shred: missing #${id}`);
  return element;
}

document.addEventListener('DOMContentLoaded', async () => {
  analytics.trackPageView('endless-shred');
  const play = byId('play', HTMLElement);
  const canvas = byId('view', HTMLCanvasElement);

  // Three.js is loaded after the page has painted, so the guide and the top bar never wait for it.
  const { SlopeView, webglAvailable, loadMountain } = await import('./ui/SlopeView.js');
  if (!webglAvailable()) {
    play.classList.add('is-unsupported');
    byId('overlay-title', HTMLElement).textContent = 'This browser cannot draw the mountain';
    byId('overlay-text', HTMLElement).textContent = 'Endless Shred needs WebGL, which is turned off or missing here. Try another browser or device.';
    byId('legend', HTMLElement).hidden = true;
    return;
  }
  // The models load alongside the game code. If they cannot load, the run still works,
  // with simpler built-in rocks and trees.
  const [{ EndlessShredGame }, mountain] = await Promise.all([
    import('./game/EndlessShredGame.js'),
    loadMountain().catch((error: unknown) => {
      console.warn('Endless Shred: could not load the mountain', error);
      return null;
    }),
  ]);

  const gates = byId('gates', HTMLElement);
  const elements = {
    stage: byId('stage', HTMLElement),
    play,
    canvas,
    score: byId('score', HTMLElement),
    best: byId('best-score', HTMLElement),
    coins: byId('coins', HTMLElement),
    bestStat: byId('best-stat', HTMLElement),
    pause: byId('pause', HTMLButtonElement),
    overlay: byId('overlay', HTMLElement),
    overlayTitle: byId('overlay-title', HTMLElement),
    overlayText: byId('overlay-text', HTMLElement),
    legend: byId('legend', HTMLElement),
    actions: byId('overlay-actions', HTMLElement),
    resume: byId('resume', HTMLButtonElement),
    restart: byId('restart', HTMLButtonElement),
    switches: byId('switches', HTMLElement),
    sound: byId('sound', HTMLButtonElement),
    music: byId('music', HTMLButtonElement),
    count: byId('count', HTMLElement),
    gradeButtons: [...document.querySelectorAll('[data-grade]')].filter((el): el is HTMLButtonElement => el instanceof HTMLButtonElement),
    gradeName: byId('grade-name', HTMLElement),
    badge: byId('badge', HTMLElement),
    section: byId('section', HTMLElement),
    pop: byId('pop', HTMLElement),
    popTitle: byId('pop-title', HTMLElement),
    popPoints: byId('pop-points', HTMLElement),
    gates,
    gateMarks: [...gates.querySelectorAll('.s-gate-mark')].filter((el): el is HTMLElement => el instanceof HTMLElement),
    burst: byId('burst', HTMLElement),
    stick: byId('stick', HTMLElement),
    air: byId('air', HTMLButtonElement),
    airTag: byId('air-tag', HTMLElement),
    hint: byId('hint', HTMLElement),
    announcer: byId('announcer', HTMLElement),
    shop: {
      open: byId('shop-open', HTMLButtonElement),
      openBoard: byId('shop-open-board', HTMLElement),
      openCoins: byId('shop-open-coins', HTMLElement),
      panel: byId('shop', HTMLElement),
      back: byId('shop-back', HTMLButtonElement),
      close: byId('shop-close', HTMLButtonElement),
      coins: byId('shop-coins', HTMLElement),
      grid: byId('shop-grid', HTMLElement),
      detail: byId('shop-detail', HTMLElement),
      portrait: byId('shop-portrait', HTMLElement),
      name: byId('shop-name', HTMLElement),
      blurb: byId('shop-blurb', HTMLElement),
      have: byId('shop-have', HTMLElement),
      price: byId('shop-price', HTMLElement),
      totalLabel: byId('shop-total-label', HTMLElement),
      total: byId('shop-total', HTMLElement),
      buy: byId('shop-buy', HTMLButtonElement),
      cancel: byId('shop-cancel', HTMLButtonElement),
    },
  };
  let game: InstanceType<typeof EndlessShredGame> | null = null;
  const view = new SlopeView(canvas, () => game?.contextLost(), mountain);
  game = new EndlessShredGame(elements, view);
  play.classList.add('is-ready');
});
